import React from 'react';
import PropTypes from 'prop-types';
import { featureFlags } from 'client/utils/feature-flags';
import { serializeError } from 'client/utils/serialize-error';

function stringifyForHtml(obj) {
  const str = JSON.stringify(obj, null, 2);
  return str
    .slice(1, -1) // remove the first `{` / last `}`
    .replace(/^ {2}/gm, '') // remove 2 spaces at the start of every line
    .replaceAll('\\n', '\n') // undo JSON.stringify's messing with line breaks
    .replace(/^(\s*)"([^"]+)":/gm, '$1$2:') // replace `"prop": ` with `prop: `
    .trim();
}

function Empty() {
  return null;
}

function getSerializedError(error) {
  if (error instanceof Error) {
    return serializeError(error);
  }

  if (error && typeof error === 'object') {
    return error; // e.g. previously serialized on server, shown on client
  }

  return { message: String(error) };
}

function EndUserSection({ endUserMessage, EndUserView, endUserViewProps }) {
  return endUserMessage ? <h4 className="my-1">{endUserMessage}</h4> : <EndUserView {...endUserViewProps} />;
}

/**
 * You may use ShowError directly or pass it to ErrorBoundary to show an error within the page's HTML. The
 * endUserMessage/EndUserView will always be shown vs. the error details - i.e., the `error` and `message` are only shown in
 * internal environments (where the `showErrors` ff is turned on).
 *
 * @param {Error} error instance of Error() or a serialized error object. Only shown in internal environments.
 * @param {string} [message] optional. custom message aside from `error.message` - i.e., logger.error(error, message). Only shown in internal environments.
 * @param {string} [endUserMessage] optional. shown to end users. shown in all environments. If provided, will be shown instead of the EndUserView.
 * @param {React.JSX.Element} [EndUserView] optional. shown to end users. shown in all environments.
 * @returns {React.JSX.Element}
 */
export function ShowError({
  error,
  message = undefined,
  endUserMessage = '',
  EndUserView = Empty,
  endUserViewProps = {},
}) {
  const { showErrors } = featureFlags.get();

  if (!showErrors) {
    return (
      <EndUserSection endUserMessage={endUserMessage} EndUserView={EndUserView} endUserViewProps={endUserViewProps} />
    );
  }

  let errorForHtml = 'could not show the error';

  try {
    const serializedError = getSerializedError(error);
    errorForHtml = stringifyForHtml(serializedError);
  } catch {
    /* empty */
  }

  return (
    <>
      <EndUserSection endUserMessage={endUserMessage} EndUserView={EndUserView} endUserViewProps={endUserViewProps} />
      {showErrors && (
        <section aria-live="assertive" role="alert" className="show-error">
          {message && (
            <h4>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{message}</pre>
            </h4>
          )}
          <h4>Error</h4>
          <pre>{errorForHtml}</pre>
        </section>
      )}
    </>
  );
}

ShowError.propTypes = {
  error: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(Error)]).isRequired,
  message: PropTypes.string,
  endUserMessage: PropTypes.string,
  EndUserView: PropTypes.func,
  endUserViewProps: PropTypes.shape({}),
};
